import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    InputAdornment,
    Stack,
    FormControl,
    Select,
    MenuItem,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import {
    Search,
    RotateCcw,
    ArrowLeft,
    FileText,
    X,
    Send,
    Download,
    Eye
} from 'lucide-react';
import GlobalPagination from '../../Components/Common/Globalpagination';

const selectMenuProps = {
    PaperProps: {
        sx: {
            boxShadow: 'none',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            mt: 0.5,
            p: 0,
            overflow: 'hidden',
            '& .MuiList-root': {
                py: 0.5,
            },
            '& .MuiMenuItem-root': {
                fontSize: '0.82rem',
                fontWeight: 500,
                py: 0.9,
                px: 1.5,
                color: '#1E293B',
                '&.Mui-selected': {
                    bgcolor: '#FFFDF9',
                    color: '#D97706',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: '#FFF8EC'
                    }
                },
                '&:hover': {
                    bgcolor: '#F8FAFC'
                }
            }
        }
    }
};

// Realistic hardcoded Host KYC Data
const INITIAL_KYC_DATA = [
    {
        id: "kyc-001",
        hostId: "host-004",
        hostName: "Dharamshala Retreat",
        hostEmail: "dh@example.com",
        businessName: "DH Wellness Pvt Ltd",
        panStatus: "submitted",
        gstStatus: "missing",
        bankUpiStatus: "verified ••4821",
        bankUpiType: "verified",
        documentsCount: "3 files",
        documents: [
            { name: "Certificate_of_Incorporation.pdf", size: "2.4 MB" },
            { name: "PAN_Card_Corporate.pdf", size: "1.1 MB" },
            { name: "Bank_Statement_HDFC.pdf", size: "3.8 MB" }
        ],
        status: "pending"
    },
    {
        id: "kyc-002",
        hostId: "host-013",
        hostName: "Varkala Yoga House",
        hostEmail: "varkala@example.com",
        businessName: "Sole proprietor",
        panStatus: "submitted",
        gstStatus: "n/a — under limit",
        bankUpiStatus: "pending",
        bankUpiType: "pending",
        documentsCount: "2 files",
        documents: [
            { name: "Aadhaar_Card_Front_Back.pdf", size: "1.8 MB" },
            { name: "PAN_Card_Individual.jpg", size: "850 KB" }
        ],
        status: "pending"
    },
    {
        id: "kyc-003",
        hostId: "host-010",
        hostName: "Aura Meditation Village",
        hostEmail: "stay@auravillage.com",
        businessName: "Aura Living LLP",
        panStatus: "submitted",
        gstStatus: "submitted",
        bankUpiStatus: "verified ••5623",
        bankUpiType: "verified",
        documentsCount: "4 files",
        documents: [
            { name: "LLP_Partnership_Deed.pdf", size: "4.2 MB" },
            { name: "GST_Registration_Certificate.pdf", size: "1.5 MB" },
            { name: "PAN_Card_LLP.pdf", size: "920 KB" },
            { name: "Cancelled_Cheque_Kotak.jpg", size: "1.2 MB" }
        ],
        status: "pending"
    },
    {
        id: "kyc-004",
        hostId: "host-011",
        hostName: "Gokarna Serene Retreats",
        hostEmail: "hello@gokarnaserenity.com",
        businessName: "Gokarna Wellness Services",
        panStatus: "submitted",
        gstStatus: "submitted",
        bankUpiStatus: "verified ••9901",
        bankUpiType: "verified",
        documentsCount: "3 files",
        documents: [
            { name: "Trade_License_Karnataka.pdf", size: "2.1 MB" },
            { name: "GSTIN_Certificate.pdf", size: "1.3 MB" },
            { name: "Bank_Proof_UnionBank.pdf", size: "1.7 MB" }
        ],
        status: "pending"
    },
    {
        id: "kyc-005",
        hostId: "host-012",
        hostName: "Shanti Forest Haven",
        hostEmail: "shanti@foresthaven.org",
        businessName: "Shanti Trust",
        panStatus: "missing",
        gstStatus: "n/a — under limit",
        bankUpiStatus: "pending",
        bankUpiType: "pending",
        documentsCount: "1 file",
        documents: [
            { name: "Trust_Registration_Deed.pdf", size: "5.1 MB" }
        ],
        status: "pending"
    },
    {
        id: "kyc-006",
        hostId: "host-001",
        hostName: "Kerala Care Centre",
        hostEmail: "kerala@example.com",
        businessName: "Kerala Care Centre Pvt Ltd",
        panStatus: "submitted",
        gstStatus: "submitted",
        bankUpiStatus: "verified ••4892",
        bankUpiType: "verified",
        documentsCount: "4 files",
        documents: [
            { name: "Company_Registration_Doc.pdf", size: "3.2 MB" },
            { name: "GST_Tax_Returns_2024.pdf", size: "2.5 MB" },
            { name: "Hospitality_Permit_Kerala.pdf", size: "1.8 MB" },
            { name: "HDFC_Corporate_Bank_Proof.pdf", size: "1.2 MB" }
        ],
        status: "approved"
    },
    {
        id: "kyc-007",
        hostId: "host-002",
        hostName: "Om Yoga Ashram",
        hostEmail: "om.yoga@example.com",
        businessName: "Om Yogic Studies Foundation",
        panStatus: "submitted",
        gstStatus: "submitted",
        bankUpiStatus: "verified ••9012",
        bankUpiType: "verified",
        documentsCount: "3 files",
        documents: [
            { name: "Society_Registration_Certificate.pdf", size: "2.9 MB" },
            { name: "PAN_Society.pdf", size: "750 KB" },
            { name: "ICICI_Cheque_Leaf.jpg", size: "900 KB" }
        ],
        status: "approved"
    },
    {
        id: "kyc-008",
        hostId: "host-003",
        hostName: "Goa Wellness Retreat",
        hostEmail: "goa@example.com",
        businessName: "Goa Holistic Living OPC",
        panStatus: "submitted",
        gstStatus: "missing",
        bankUpiStatus: "verified ••3421",
        bankUpiType: "verified",
        documentsCount: "2 files",
        documents: [
            { name: "OPC_Incorporation.pdf", size: "1.9 MB" },
            { name: "SBI_Account_Statement.pdf", size: "2.3 MB" }
        ],
        status: "pending"
    }
];

const HostKyc = ({ selectedHost, onBack }) => {
    const [kycList, setKycList] = useState(INITIAL_KYC_DATA);
    const [searchTerm, setSearchTerm] = useState(selectedHost?.name || "");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);

    // Toast Notification
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");
    const [toastOpen, setToastOpen] = useState(false);

    // Request Info Dialog
    const [requestInfoHost, setRequestInfoHost] = useState(null);
    const [requestMessage, setRequestMessage] = useState("");

    // Documents Dialog
    const [documentsHost, setDocumentsHost] = useState(null);

    // Handle View Document
    const handleViewDocument = (docName) => {
        // Create mock content for the document
        const mockContent = `
This is a mock preview of: ${docName}

Document Type: KYC Verification Document
Host: ${documentsHost?.hostName}
Generated: ${new Date().toLocaleDateString()}

---
DOCUMENT PREVIEW
This would display the actual document content in a real application.
For PDF files, this would show a PDF viewer.
For images, this would show the image in a modal.
For other files, this would show a file preview.

---
END OF PREVIEW
        `;
        
        // Create a new window to display the document
        const newWindow = window.open('', '_blank');
        newWindow.document.write(`
            <html>
                <head>
                    <title>View: ${docName}</title>
                    <style>
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            padding: 20px;
                            background: #f5f5f5;
                            margin: 0;
                        }
                        .preview-container {
                            background: white;
                            padding: 30px;
                            border-radius: 8px;
                            max-width: 800px;
                            margin: 0 auto;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        pre {
                            white-space: pre-wrap;
                            word-wrap: break-word;
                            font-family: 'Courier New', monospace;
                            font-size: 14px;
                            line-height: 1.6;
                            color: #333;
                        }
                        h1 {
                            color: #1E293B;
                            margin-top: 0;
                        }
                    </style>
                </head>
                <body>
                    <div class="preview-container">
                        <h1>Document Preview</h1>
                        <pre>${mockContent}</pre>
                    </div>
                </body>
            </html>
        `);
        newWindow.document.close();

        setToastMessage(`Viewing ${docName}...`);
        setToastSeverity("info");
        setToastOpen(true);
    };

    // Handle Download Document
    const handleDownloadDocument = (docName) => {
        // Create mock file content
        const mockContent = `
DOCUMENT: ${docName}
HOST: ${documentsHost?.hostName}
EMAIL: ${documentsHost?.hostEmail}
DATE: ${new Date().toISOString()}

This is a mock document file for KYC verification purposes.
In a real application, this would download the actual file from the server.
        `;
        
        // Create a blob and trigger download
        const blob = new Blob([mockContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = docName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setToastMessage(`Downloaded ${docName}`);
        setToastSeverity("success");
        setToastOpen(true);
    };

    // Filter computation
    const filteredKycList = useMemo(() => {
        return kycList.filter((item) => {
            // Search filter
            if (searchTerm.trim()) {
                const q = searchTerm.toLowerCase().trim();
                const hostMatch = item.hostName.toLowerCase().includes(q);
                const emailMatch = item.hostEmail.toLowerCase().includes(q);
                const businessMatch = item.businessName.toLowerCase().includes(q);
                if (!hostMatch && !emailMatch && !businessMatch) return false;
            }

            // Status filter
            if (statusFilter !== "all") {
                if (statusFilter === "approved" && item.status !== "approved") return false;
                if (statusFilter === "pending" && item.status !== "pending") return false;
                if (statusFilter === "requested" && item.status !== "info_requested") return false;
            }

            return true;
        });
    }, [kycList, searchTerm, statusFilter]);

    // Pagination
    const totalPages = Math.ceil(filteredKycList.length / limit) || 1;
    const paginatedKycList = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredKycList.slice(start, start + limit);
    }, [filteredKycList, page, limit]);

    // Handle Approve KYC
    const handleApproveKyc = (id, hostName) => {
        setKycList(prev => prev.map(item => {
            if (item.id === id) {
                return { ...item, status: "approved" };
            }
            return item;
        }));
        setToastMessage(`KYC approved for ${hostName}. Host is now eligible for payouts.`);
        setToastSeverity("success");
        setToastOpen(true);
    };

    // Handle Open Request Info Dialog
    const handleOpenRequestInfo = (item) => {
        setRequestInfoHost(item);
        setRequestMessage(`Dear ${item.hostName},\n\nPlease provide the following missing documents to complete your KYC verification:\n- Valid GST certificate / Declaration\n- Updated Bank Account Statement`);
    };

    // Handle Send Request Info
    const handleSendRequestInfo = () => {
        if (!requestInfoHost) return;
        setKycList(prev => prev.map(item => {
            if (item.id === requestInfoHost.id) {
                return { ...item, status: "info_requested" };
            }
            return item;
        }));
        setToastMessage(`Information request sent to ${requestInfoHost.hostEmail}.`);
        setToastSeverity("info");
        setToastOpen(true);
        setRequestInfoHost(null);
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setPage(1);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* ── Top Back Button ── */}
            <Button
                onClick={onBack}
                startIcon={<ArrowLeft size={16} />}
                disableElevation
                disableRipple
                sx={{
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    color: '#64748B',
                    px: 0,
                    py: 0.5,
                    mb: 2,
                    '&:hover': {
                        bgcolor: 'transparent',
                        color: '#1E293B',
                    }
                }}
            >
                Back to Hosts
            </Button>

            {/* ── Header Title & Subtitle ── */}
            <Box sx={{ mb: 2.5 }}>
                <Typography
                    sx={{
                        color: '#1E293B',
                        fontWeight: 600,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        lineHeight: 1.2
                    }}
                >
                    Host KYC Verification
                </Typography>
                <Typography sx={{ color: '#64748B', fontSize: '0.82rem', mt: 0.5 }}>
                    {filteredKycList.length} partners awaiting verification {selectedHost ? `· Filtered for: ${selectedHost.name}` : ''}
                </Typography>
            </Box>

            {/* ── Callout Notice Box with Left Accent Bar ── */}
            <Box
                sx={{
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderLeft: '4px solid #803927',
                    borderRadius: '6px',
                    p: 2,
                    mb: 3,
                }}
            >
                <Typography sx={{ fontSize: '0.84rem', color: '#1E293B', lineHeight: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#803927', mr: 0.75 }}>
                        Note:
                    </Box>
                    KYC must pass before a host can receive a payout. Documents are stored encrypted; only Master Admin and Finance roles can view them.
                </Typography>
            </Box>

            {/* ── Search & Filter Controls ── */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
                    {/* Search Input */}
                    <TextField
                        placeholder="Search by host, business name, email..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                                    <Search size={16} color="#94A3B8" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '1 1 220px', md: '0 1 260px' },
                            bgcolor: '#FFFFFF',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            },
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            displayEmpty
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) => {
                                if (!selected || selected === 'all') return <span style={{ color: '#64748B' }}>All Statuses</span>;
                                if (selected === 'pending') return 'Pending Review';
                                if (selected === 'approved') return 'Approved';
                                if (selected === 'requested') return 'Info Requested';
                                return selected;
                            }}
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value="pending">Pending Review</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="requested">Info Requested</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Reset Button */}
                    {(searchTerm || statusFilter !== 'all') && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<RotateCcw size={14} />}
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                height: '38px',
                                px: 1.5,
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                '&:hover': {
                                    bgcolor: '#F8FAFC',
                                    borderColor: '#CBD5E1',
                                    color: '#1E293B'
                                }
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* ── KYC Table ── */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    bgcolor: '#FFFFFF',
                    overflow: "hidden",
                    boxShadow: 'none'
                }}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 2 }}>HOST</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5 }}>BUSINESS NAME</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>PAN</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>GST</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>BANK / UPI</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>DOCUMENTS</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 2, textAlign: 'center' }}>ACTION</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedKycList.length > 0 ? (
                                paginatedKycList.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        sx={{
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}
                                    >
                                        {/* HOST */}
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>
                                                {item.hostName}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.2 }}>
                                                {item.hostEmail}
                                            </Typography>
                                        </TableCell>

                                        {/* BUSINESS NAME */}
                                        <TableCell sx={{ py: 1.5, px: 1.5, fontSize: '0.82rem', color: '#334155' }}>
                                            {item.businessName}
                                        </TableCell>

                                        {/* PAN */}
                                        <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center' }}>
                                            <Box
                                                sx={{
                                                    px: 1.2,
                                                    py: 0.35,
                                                    borderRadius: '20px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block',
                                                    bgcolor: item.panStatus === 'submitted' ? '#E8F5E8' : '#FEF2F2',
                                                    color: item.panStatus === 'submitted' ? '#2E7D32' : '#DC2626',
                                                    border: item.panStatus === 'submitted' ? '1px solid #C8E6C9' : '1px solid #FECACA',
                                                    textTransform: 'lowercase'
                                                }}
                                            >
                                                {item.panStatus}
                                            </Box>
                                        </TableCell>

                                        {/* GST */}
                                        <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center' }}>
                                            <Box
                                                sx={{
                                                    px: 1.2,
                                                    py: 0.35,
                                                    borderRadius: '20px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block',
                                                    bgcolor: item.gstStatus === 'submitted'
                                                        ? '#E8F5E8'
                                                        : item.gstStatus === 'missing'
                                                            ? '#FEF3C7'
                                                            : '#F1F5F9',
                                                    color: item.gstStatus === 'submitted'
                                                        ? '#2E7D32'
                                                        : item.gstStatus === 'missing'
                                                            ? '#D97706'
                                                            : '#64748B',
                                                    border: item.gstStatus === 'submitted'
                                                        ? '1px solid #C8E6C9'
                                                        : item.gstStatus === 'missing'
                                                            ? '1px solid #FDE68A'
                                                            : '1px solid #E2E8F0',
                                                    textTransform: 'lowercase'
                                                }}
                                            >
                                                {item.gstStatus}
                                            </Box>
                                        </TableCell>

                                        {/* BANK / UPI */}
                                        <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center' }}>
                                            <Box
                                                sx={{
                                                    px: 1.2,
                                                    py: 0.35,
                                                    borderRadius: '20px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    display: 'inline-block',
                                                    bgcolor: item.bankUpiType === 'verified' ? '#E8F5E8' : '#FEF3C7',
                                                    color: item.bankUpiType === 'verified' ? '#2E7D32' : '#D97706',
                                                    border: item.bankUpiType === 'verified' ? '1px solid #C8E6C9' : '1px solid #FDE68A',
                                                    textTransform: 'lowercase'
                                                }}
                                            >
                                                {item.bankUpiStatus}
                                            </Box>
                                        </TableCell>

                                        {/* DOCUMENTS */}
                                        <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center' }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setDocumentsHost(item)}
                                                disableElevation
                                                disableRipple
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    color: '#803927',
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    '&:hover': {
                                                        bgcolor: '#FFF8EC',
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                    <FileText size={14} color="#803927" />
                                                    <span>{item.documentsCount}</span>
                                                </Stack>
                                            </Button>
                                        </TableCell>

                                        {/* ACTION (Approve KYC and Request info side by side) */}
                                        <TableCell sx={{ py: 1.5, px: 2, textAlign: 'center' }}>
                                            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={item.status === 'approved'}
                                                    onClick={() => handleApproveKyc(item.id, item.hostName)}
                                                    disableElevation
                                                    disableFocusRipple
                                                    disableRipple
                                                    disableTouchRipple
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        color: '#FFFFFF',
                                                        bgcolor: item.status === 'approved' ? '#94A3B8' : '#4E7A58',
                                                        border: '1px solid transparent',
                                                        borderRadius: '4px',
                                                        height: '28px',
                                                        py: 0,
                                                        px: 1.4,
                                                        whiteSpace: 'nowrap',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            bgcolor: '#3B6044',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    {item.status === 'approved' ? 'Approved' : 'Approve KYC'}
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleOpenRequestInfo(item)}
                                                    disableElevation
                                                    disableFocusRipple
                                                    disableRipple
                                                    disableTouchRipple
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        color: '#C0392B',
                                                        borderColor: '#C0392B',
                                                        borderRadius: '4px',
                                                        height: '28px',
                                                        py: 0,
                                                        px: 1.4,
                                                        whiteSpace: 'nowrap',
                                                        bgcolor: '#FFFFFF',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            bgcolor: '#FEF2F2',
                                                            borderColor: '#A93226',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    Request info
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.8rem' }}>
                                        No KYC records found matching your filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* ── Pagination ── */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={filteredKycList.length}
                totalPages={totalPages}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1);
                }}
            />

            {/* ── Request Info Dialog ── */}
            <Dialog
                open={Boolean(requestInfoHost)}
                onClose={() => setRequestInfoHost(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                            Request Additional Documents
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.2 }}>
                            {requestInfoHost?.hostName} ({requestInfoHost?.hostEmail})
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setRequestInfoHost(null)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#475569', mb: 1 }}>
                        Message to Host:
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={requestMessage}
                        onChange={(e) => setRequestMessage(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.8rem',
                                borderRadius: '6px',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#803927' }
                            }
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setRequestInfoHost(null)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            color: '#64748B',
                            borderColor: '#E2E8F0',
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#F8FAFC' }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSendRequestInfo}
                        startIcon={<Send size={14} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            bgcolor: '#803927',
                            color: '#FFFFFF',
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#6A2E1F', boxShadow: 'none' }
                        }}
                    >
                        Send Request
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Documents Dialog ── */}
            <Dialog
                open={Boolean(documentsHost)}
                onClose={() => setDocumentsHost(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                            KYC Documents
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.2 }}>
                            {documentsHost?.hostName} ({documentsHost?.documents?.length || 0} files)
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setDocumentsHost(null)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={1.5}>
                        {documentsHost?.documents?.map((doc, index) => (
                            <Box
                                key={index}
                                sx={{
                                    p: 1.5,
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1E293B', mb: 0.25 }}>
                                        {doc.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                                        {doc.size}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.75}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Eye size={12} />}
                                        onClick={() => handleViewDocument(doc.name)}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.72rem',
                                            fontWeight: 500,
                                            color: '#64748B',
                                            borderColor: '#E2E8F0',
                                            borderRadius: '4px',
                                            py: 0.5,
                                            px: 1,
                                            minWidth: 'auto',
                                            '&:hover': {
                                                bgcolor: '#F1F5F9',
                                                borderColor: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Download size={12} />}
                                        onClick={() => handleDownloadDocument(doc.name)}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.72rem',
                                            fontWeight: 500,
                                            color: '#64748B',
                                            borderColor: '#E2E8F0',
                                            borderRadius: '4px',
                                            py: 0.5,
                                            px: 1,
                                            minWidth: 'auto',
                                            '&:hover': {
                                                bgcolor: '#F1F5F9',
                                                borderColor: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        Download
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDocumentsHost(null)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            color: '#64748B',
                            borderColor: '#E2E8F0',
                            borderRadius: '4px',
                            '&:hover': { bgcolor: '#F8FAFC' }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Toast Snackbar ── */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setToastOpen(false)}
                    severity={toastSeverity}
                    sx={{
                        bgcolor: '#1E293B',
                        color: '#FFFFFF',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        boxShadow: 'none',
                        '& .MuiAlert-icon': { color: '#FFBD65' }
                    }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HostKyc;
